/* eslint-disable no-param-reassign */
import {
  cardinalForExposure,
  exposureForCardinal
} from '@alpinehomeair/common';
import store from 'app/store';
import {
  airHandlerTypeCodes,
  DETACHED_SPACE_CODES,
  STRUCTURE_GRID_SIZE
} from 'data/constants';
import {
  defaultAboveGradeWall,
  defaultBasementFloor,
  defaultBelowGradeWall,
  defaultCeilingBelowAttic,
  defaultCeilingBelowRoof,
  defaultCeilingOnExposedBeams,
  defaultDoor,
  defaultFloorOverExposedSpace,
  defaultFloorOverUnconditionedSpace,
  defaultGlass,
  defaultSizingAttributes,
  defaultSlabFloor
} from 'data/sizing-calculator-defaults';
import {
  AboveGradeWallType,
  AirHandlerTypeCodeType,
  ApplianceType,
  AttributeType,
  BelowGradeWallType,
  DoorItemType,
  EdgeItemsType,
  ExposureDirectionType,
  FootprintPolygonsType,
  GeneratedSpaceType,
  GlassItemType,
  LevelForDatabaseType,
  LevelType,
  LevelTypeType,
  PositionAndDimensionsType,
  PositionType,
  ProjectAttachmentType,
  SectionType,
  SizingCalculatorAttributesType,
  SpacePositionData,
  SpaceType,
  TruncatedLevelType,
  ZoneType
} from 'data/types';
import {
  setStructure,
  StructureImageAssignmentType
} from 'features/structure/structure-slice';
import { produce } from 'immer';
import { nanoid } from 'nanoid';
import {
  getRestrictedAirHandlerTypesForSection as computeRestrictedAirHandlerTypesForSection,
  getRestrictedAirHandlerTypesForSpace as computeRestrictedAirHandlerTypesForSpace
} from 'utils/air-handler-restrictions';
import { restoreAttributes, truncateAttributes } from 'utils/attributes';
// TODO: Break circular dependency (utils/structure <-> utils/data)
import { getItem } from 'utils/data';
import { setQueryParam } from 'utils/helpers';
import { normalizeOpeningOnWall } from 'utils/opening-position';
import { OpeningRunBoundsType } from 'utils/opening-position-types';
import { getProjectSettingValue, isDuctedPathway } from 'utils/project';
import { resizeWindowOnWalls } from 'utils/resize-window-on-walls';
// TODO: Break circular dependency (utils/structure <-> utils/ui)
import { errorModal } from 'utils/ui';
import { WallOpeningItemType } from 'utils/wall-opening-item-types';
import {
  clampRectToFootprint,
  isPointInPolygon,
  isRectInPolygon,
  polygonBoundingBox
} from './footprint';
import { findContainingPolygon } from './footprint-polygons';
import {
  countsTowardFootprintSquareFootage,
  isLevelConditioned
} from './level-conditioning';
import { polygonsOverlapStrict, rectToAbsolutePolygon } from './space-polygon';
import {
  applyPolygonHorizontalShrink,
  applyPolygonVerticalShrink,
  findOpenAreaFit,
  polygonsOverlapRect
} from './space-rect-shrink';
import { validateSizingAttributes } from './validation';

export { countsTowardFootprintSquareFootage, isLevelConditioned };

export type EdgeType = 'top' | 'right' | 'bottom' | 'left';

const EDGE_TO_EXPOSURE_MAP: Record<EdgeType, ExposureDirectionType> = {
  top: 'North',
  right: 'East',
  bottom: 'South',
  left: 'West'
};

const EXPOSURE_TO_EDGE_MAP: Record<string, EdgeType> = {
  North: 'top',
  East: 'right',
  South: 'bottom',
  West: 'left'
};

export function edgeToExposure(edge: EdgeType): ExposureDirectionType {
  return EDGE_TO_EXPOSURE_MAP[edge];
}

export function exposureToEdge(exposure: string): EdgeType | undefined {
  return EXPOSURE_TO_EDGE_MAP[exposure];
}

export function getOrientedEdge(
  exposure: string,
  structureOrientation: ExposureDirectionType | undefined
): EdgeType | undefined {
  return cardinalForExposure(exposure, structureOrientation) ?? undefined;
}

export function getOrientedExposure(
  edge: EdgeType,
  structureOrientation: ExposureDirectionType | undefined
): ExposureDirectionType {
  return exposureForCardinal(edge, structureOrientation);
}

export function checkRectsOverlap(
  rect1: PositionAndDimensionsType,
  rect2: PositionAndDimensionsType
): boolean {
  return (
    rect1.x < rect2.x + rect2.width &&
    rect1.x + rect1.width > rect2.x &&
    rect1.y < rect2.y + rect2.height &&
    rect1.y + rect1.height > rect2.y
  );
}

export function normalizeGridPosition(
  position: PositionAndDimensionsType,
  gridSize: number = STRUCTURE_GRID_SIZE
): PositionAndDimensionsType {
  return {
    x: position.x / gridSize,
    y: position.y / gridSize,
    width: position.width / gridSize,
    height: position.height / gridSize
  };
}

export function findSpaceInStructure(
  structure: LevelType[],
  spaceId: string
): SpaceType | undefined {
  for (const level of structure) {
    for (const section of level.sections) {
      for (const space of section.spaces) {
        if (space.uniqueId === spaceId) {
          return space;
        }
      }
    }
  }
  return undefined;
}

function getWallRenderedEdge(
  wall: AboveGradeWallType | BelowGradeWallType,
  orientation: ExposureDirectionType | undefined
): EdgeType | undefined {
  if ('edge' in wall && wall.edge) {
    return wall.edge;
  }
  return getOrientedEdge(wall.exposure, orientation);
}

function wallIsOnEdge(
  wall: AboveGradeWallType | BelowGradeWallType,
  edge: EdgeType,
  orientation: ExposureDirectionType | undefined
): boolean {
  return getWallRenderedEdge(wall, orientation) === edge;
}

export function removeItemFromWalls<
  T extends AboveGradeWallType | BelowGradeWallType
>(
  walls: T[],
  edge: EdgeType | undefined,
  itemId: string | number,
  itemType: 'window' | 'door',
  orientation?: ExposureDirectionType
): T[] {
  return walls.map((wall) => {
    if (edge && !wallIsOnEdge(wall, edge, orientation)) return wall;

    if (itemType === 'window') {
      return {
        ...wall,
        verticalGlass: wall.verticalGlass?.filter((w) => w.uniqueId !== itemId)
      };
    }
    return {
      ...wall,
      solidDoors: wall.solidDoors?.filter((d) => d.uniqueId !== itemId)
    };
  });
}

export function updateWindowSize(
  sizingAttributes: SpaceType['sizingAttributes'],
  edge: EdgeType,
  windowId: string,
  newWidth: number,
  newHeight: number,
  runBounds: OpeningRunBoundsType,
  structureOrientation?: ExposureDirectionType
): SpaceType['sizingAttributes'] {
  if (!sizingAttributes) return sizingAttributes;

  const onEdge = (wall: AboveGradeWallType | BelowGradeWallType) =>
    wallIsOnEdge(wall, edge, structureOrientation);

  return {
    ...sizingAttributes,
    aboveGradeWalls: resizeWindowOnWalls(
      sizingAttributes.aboveGradeWalls,
      onEdge,
      windowId,
      newWidth,
      newHeight,
      runBounds
    ),
    belowGradeWalls: resizeWindowOnWalls(
      sizingAttributes.belowGradeWalls,
      onEdge,
      windowId,
      newWidth,
      newHeight,
      runBounds
    )
  };
}

export function deleteEdgeItem(
  sizingAttributes: SpaceType['sizingAttributes'],
  edge: EdgeType,
  itemId: string,
  isWindow: boolean,
  structureOrientation?: ExposureDirectionType
): SpaceType['sizingAttributes'] {
  if (!sizingAttributes) return sizingAttributes;

  const itemType = isWindow ? 'window' : 'door';

  return {
    ...sizingAttributes,
    aboveGradeWalls: removeItemFromWalls(
      sizingAttributes.aboveGradeWalls,
      edge,
      itemId,
      itemType,
      structureOrientation
    ),
    belowGradeWalls: removeItemFromWalls(
      sizingAttributes.belowGradeWalls,
      edge,
      itemId,
      itemType,
      structureOrientation
    )
  };
}

function findOpeningTargetWallIndex<
  T extends AboveGradeWallType | BelowGradeWallType
>(
  walls: T[],
  edge: EdgeType,
  orientation: ExposureDirectionType | undefined,
  targetWallUniqueId: string | undefined
): number {
  const targetIndex = targetWallUniqueId
    ? walls.findIndex((wall) => wall.uniqueId === targetWallUniqueId)
    : -1;
  if (targetIndex !== -1) {
    return targetIndex;
  }
  return walls.findIndex((wall) => wallIsOnEdge(wall, edge, orientation));
}

export function addItemToWalls<
  T extends AboveGradeWallType | BelowGradeWallType
>(
  walls: T[],
  edge: EdgeType,
  item: WallOpeningItemType,
  itemType: 'window' | 'door',
  orientation: ExposureDirectionType | undefined,
  runBounds: OpeningRunBoundsType,
  targetWallUniqueId?: string
): T[] {
  const targetIndex = findOpeningTargetWallIndex(
    walls,
    edge,
    orientation,
    targetWallUniqueId
  );
  if (targetIndex === -1) {
    return walls;
  }

  const wall = walls[targetIndex];
  const nextItem = normalizeOpeningOnWall(item, runBounds);
  const nextWall =
    itemType === 'window'
      ? {
          ...wall,
          verticalGlass: [
            ...(wall.verticalGlass || []),
            nextItem as GlassItemType
          ]
        }
      : ({
          ...wall,
          solidDoors: [...(wall.solidDoors || []), nextItem as DoorItemType]
        } as T);

  return walls.map((entry, index) =>
    index === targetIndex ? nextWall : entry
  );
}

export function findSpaceById(
  structure: LevelType[],
  id: string
): SpaceType | null {
  for (const level of structure) {
    for (const section of level.sections) {
      for (const space of section.spaces) {
        if (space.uniqueId === id) {
          return space;
        }
      }
    }
  }
  return null;
}

export function findStructureItemById(
  structure: LevelType[],
  id: string
): LevelType | SectionType | SpaceType | null {
  for (const level of structure) {
    if (level.uniqueId === id) {
      return level;
    }
    for (const section of level.sections) {
      if (section.uniqueId === id) {
        return section;
      }
      for (const space of section.spaces) {
        if (space.uniqueId === id) {
          return space;
        }
      }
    }
  }
  return null;
}

export function findSectionBySpaceId(
  structure: LevelType[],
  id: string
): SectionType | null {
  for (const level of structure) {
    for (const section of level.sections) {
      for (const space of section.spaces) {
        if (space.uniqueId === id) {
          return section;
        }
      }
    }
  }
  return null;
}

export function findLevelBySpaceId(
  structure: LevelType[],
  id: string
): LevelType | null {
  for (const level of structure) {
    if (
      level.sections.some((section) =>
        section.spaces.some((space) => space.uniqueId === id)
      )
    ) {
      return level;
    }
  }
  return null;
}

export function findCountOfItem(
  structure: LevelType[],
  itemId: number
): number {
  let count = 0;
  for (const level of structure) {
    for (const section of level.sections) {
      for (const space of section.spaces) {
        if (space.itemId === itemId) {
          count += 1;
        }
      }
    }
  }
  return count;
}

export function getAllLabelsOfType(
  type: 'level' | 'section' | 'space'
): string[] {
  const { structure } = store.getState().structure;

  switch (type) {
    case 'level':
      return structure.map((level) => level.label);
    case 'section':
      return structure.flatMap((level) =>
        level.sections.map((section) => section.label)
      );
    case 'space':
      return structure.flatMap((level) =>
        level.sections.flatMap((section) =>
          section.spaces.map((space) => space.label)
        )
      );
    default:
      return [];
  }
}

export function incrementItemLabel(
  type: 'level' | 'section' | 'space',
  itemLabel: string,
  incrementFirstOfKind = false
) {
  const existingLabels = getAllLabelsOfType(type);
  let itemNumber = existingLabels.includes(itemLabel) ? 2 : 1;

  while (existingLabels.includes(`${itemLabel} ${itemNumber}`)) {
    itemNumber += 1;
  }

  return itemNumber === 1 && !incrementFirstOfKind
    ? itemLabel
    : `${itemLabel} ${itemNumber}`;
}

function inheritAttributes(
  parentAttributes: AttributeType[],
  childAttributes: AttributeType[]
): AttributeType[] {
  const newAttributes = [...childAttributes];
  const newAttributeIds = newAttributes.map((a) => a.id);
  parentAttributes.forEach((att) => {
    if (newAttributeIds.includes(att.id) && att.inheritable) {
      const attIndex = newAttributes.findIndex((na) => na.id === att.id);
      if (attIndex !== -1 && (att.value || att.value === 0)) {
        newAttributes[attIndex] = {
          ...newAttributes[attIndex],
          value: att.value
        };
      }
    }
  });

  const levelCeilingType = parentAttributes.find(
    (att) => att.code === 'level_ceiling_type'
  );
  const ceilingTypeIndex = newAttributes.findIndex(
    (att) => att.code === 'ceiling_type'
  );
  if (levelCeilingType && ceilingTypeIndex !== -1) {
    newAttributes[ceilingTypeIndex] = {
      ...newAttributes[ceilingTypeIndex],
      value:
        levelCeilingType.value === 'Varies' ? undefined : levelCeilingType.value
    };
  }

  return newAttributes;
}

export function createLevel(
  levelType: LevelTypeType,
  options?: { data?: Partial<LevelType>; attributesToInherit?: AttributeType[] }
): LevelType | null {
  const item = getItem(levelType);

  if (!item) {
    console.error('Level item not found');
    errorModal(`Level item not found`);
    return null;
  }

  const newAttributes = options?.attributesToInherit
    ? inheritAttributes(options.attributesToInherit, item.attributes)
    : [...item.attributes];

  return {
    uniqueId: nanoid(5),
    type: levelType,
    label: incrementItemLabel('level', item.label, item.code === 'level'),
    sections: [],
    attributes: newAttributes,
    electricalPanels: [],
    valid: true,
    modified: false,
    conditioned: levelType === 'level',
    ...options?.data
  };
}

export function createSection(data?: Partial<SectionType>): SectionType {
  return {
    uniqueId: nanoid(5),
    label: incrementItemLabel('section', 'Proximity Area', true),
    spaces: [],
    attributes: [],
    valid: true,
    ...data
  };
}

export function createSpace(
  itemId: number,
  options?: { data?: Partial<SpaceType>; attributesToInherit?: AttributeType[] }
): SpaceType | null {
  const { applianceItems } = store.getState().data;
  const item = getItem(itemId);

  if (!item) {
    console.error('Space item not found');
    errorModal(`Space item not found`);
    return null;
  }

  const newAttributes = options?.attributesToInherit
    ? inheritAttributes(options.attributesToInherit, item.attributes)
    : [...item.attributes];

  const newSpace: SpaceType = {
    uniqueId: nanoid(5),
    label: incrementItemLabel('space', item.label),
    itemId: item.id,
    attributes: newAttributes,
    BTUs: {
      heating: 0,
      cooling: 0
    },
    airHandlerRankings: [],
    valid: true,
    modified: false,
    sizingAttributes: {
      ...defaultSizingAttributes,
      appliances: item.defaultAppliances
        .map((code) => {
          const applianceItem = applianceItems.find((a) => a.code === code);
          return applianceItem
            ? {
                ...applianceItem,
                custom: false,
                uniqueId: nanoid(5)
              }
            : null;
        })
        .filter((a) => a) as ApplianceType[]
    },
    conditioned: !!item.canBeAlone,
    openTo: [],
    ...options?.data
  };
  return newSpace;
}

export function duplicateSizingAttributes(
  sizingAttributes: SizingCalculatorAttributesType
): SizingCalculatorAttributesType {
  const newSizingAttributes: SizingCalculatorAttributesType = {
    ...sizingAttributes
  };

  Object.entries(sizingAttributes).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      newSizingAttributes[key] = value.map((item) => ({
        ...item,
        uniqueId: nanoid(5)
      }));
    }
  });
  return newSizingAttributes;
}

export function duplicateSpace(
  space: SpaceType,
  keepSameName?: boolean
): SpaceType {
  const item = getItem(space.itemId);

  if (!item) {
    console.error('Space item not found');
    errorModal(`Space item not found`);
    return space;
  }

  return {
    ...space,
    uniqueId: nanoid(5),
    label: keepSameName ? space.label : incrementItemLabel('space', item.label),
    sizingAttributes: space.sizingAttributes
      ? duplicateSizingAttributes(space.sizingAttributes)
      : undefined,
    modified: false
  };
}

export function getRestrictedAirHandlerTypes(
  zone: ZoneType,
  structure: LevelType[]
) {
  if (zone.type === 'ducted') {
    return Object.values(airHandlerTypeCodes).filter((c) => c !== 'ducted');
  }
  const restrictedTypes: AirHandlerTypeCodeType[] = ['ducted', 'evap_coil'];
  if (getProjectSettingValue('joist_width') === '16"') {
    restrictedTypes.push('cassette');
  }
  const zoneItemsCount = zone.zoneItems.length;
  zone.zoneItems.forEach((zoneItem) => {
    const space = structure
      .find((level) =>
        level.sections.some((section) =>
          section.spaces.some((sp) => sp.uniqueId === zoneItem.structureItemId)
        )
      )
      ?.sections.find((section) =>
        section.spaces.some((sp) => sp.uniqueId === zoneItem.structureItemId)
      )
      ?.spaces.find((sp) => sp.uniqueId === zoneItem.structureItemId);
    if (space) {
      const attributeValues = Object.fromEntries(
        space.attributes.map((a) => [a.code, a.value])
      );
      const item = getItem(space.itemId);
      if (item && item.allowedAirHandlerTypes.length > 0) {
        const airHandlerTypes = Object.values(airHandlerTypeCodes);
        restrictedTypes.push(
          ...airHandlerTypes.filter(
            (type) => !item.allowedAirHandlerTypes.includes(type)
          )
        );
      }
      if (
        attributeValues.ceiling_type === 'Vaulted' ||
        attributeValues.ceiling_type === 'Sloped' ||
        attributeValues.space_above !== 'Yes'
      ) {
        restrictedTypes.push('cassette', 'cassette_16');
      }
      if (attributeValues.ceiling_type !== 'Drop Ceiling') {
        restrictedTypes.push('cassette_drop_ceiling');
      }
      if (attributeValues.ceiling_height < 6) {
        restrictedTypes.push('wall', 'wallDIY', 'heatingConsole');
      }
      if (
        attributeValues.space_above !== 'Yes' &&
        attributeValues.space_below !== 'Yes'
      ) {
        restrictedTypes.push('concealed');
      }
      if (attributeValues.share_air === 'No' && zoneItemsCount > 1) {
        restrictedTypes.push(
          'wall',
          'cassette',
          'heatingConsole',
          'console',
          'wallDIY',
          'cassette_16',
          'cassette_drop_ceiling'
        );
      }
    }
  });
  return Array.from(new Set(restrictedTypes));
}

export function getRestrictedAirHandlerTypesForSpace(
  spaceId: string,
  structure: LevelType[]
): AirHandlerTypeCodeType[] {
  return computeRestrictedAirHandlerTypesForSpace(spaceId, structure);
}

export function getRestrictedAirHandlerTypesForSection(
  sectionSpaceIds: string[],
  structure: LevelType[]
): AirHandlerTypeCodeType[] {
  return computeRestrictedAirHandlerTypesForSection(sectionSpaceIds, structure);
}

export const DRAG_TYPE_TO_AIR_HANDLER_CODE: Record<
  string,
  AirHandlerTypeCodeType
> = {
  'new-air-handler-high-wall': 'wall',
  'new-air-handler-low-wall': 'heatingConsole',
  'new-air-handler-floor-ceiling': 'console',
  'new-air-handler-ceiling-cassette': 'cassette',
  'new-air-handler-ceiling-cassette-slim-fit': 'cassette_16',
  'new-air-handler-concealed-duct': 'concealed',
  'new-air-handler-ducted': 'ducted'
};

export function getAirHandlerTypeCodeFromDragType(
  dragType: string
): AirHandlerTypeCodeType | null {
  return DRAG_TYPE_TO_AIR_HANDLER_CODE[dragType] ?? null;
}

export function getAirHandlerTypeLabelFromCode(
  code: AirHandlerTypeCodeType
): string | null {
  const entry = Object.entries(airHandlerTypeCodes).find(
    ([, value]) => value === code
  );
  return entry ? entry[0] : null;
}

function filterSizingFields(item: any): any {
  const baseSubFields = [
    'width',
    'height',
    'length',
    'area',
    'ceilingType',
    'exposure'
  ];
  const filtered: any = {};
  baseSubFields.forEach((field) => {
    if (field in item) filtered[field] = item[field];
  });
  if ('verticalGlass' in item && Array.isArray(item.verticalGlass)) {
    filtered.verticalGlass = item.verticalGlass.map(filterSizingFields);
  }
  if ('solidDoors' in item && Array.isArray(item.solidDoors)) {
    filtered.solidDoors = item.solidDoors.map(filterSizingFields);
  }
  return filtered;
}

export const simplifyStructure = (structure: LevelType[]) => {
  let currentLevel = structure.length;
  const hasAttic = structure.some((level) => level.label === 'Attic');
  const hasCrawlspace = structure.some((level) => level.label === 'Crawlspace');
  const hasBasement = structure.some((level) => level.label === 'Basement');

  return structure.map((level) => {
    const isTopFloor =
      (currentLevel === structure.length && !hasAttic) ||
      (currentLevel === structure.length - 1 && hasAttic);
    const isBottomFloor =
      (currentLevel === 1 && !hasBasement && !hasCrawlspace) ||
      (currentLevel === 2 && hasBasement) ||
      (currentLevel === 2 && hasCrawlspace);
    const isBasementLevel = level.label === 'Basement';
    const isBelowAttic = hasAttic && isTopFloor;
    const isBelowRoof = !hasAttic && isTopFloor;
    const isAboveCrawlspace = hasCrawlspace && isBottomFloor;
    const isAboveBasement = hasBasement && isBottomFloor;
    const isBasementConditioned =
      getProjectSettingValue('basement_type') === 'Finished';
    const { attributes, modified, uniqueId, ...newLevel } = {
      ...level,
      sections: level.sections.map((section) => {
        const {
          uniqueId: sectionUniqueId,
          attributes: sectionAttributes,
          ...newSection
        } = section;
        return {
          ...newSection,
          spaces: section.spaces.map((space) => {
            const {
              attributes: spaceAttributes,
              modified: spaceModified,
              airHandlerRankings,
              BTUs,
              valid,
              loading: spaceLoading,
              ...newSpace
            } = {
              ...space
            };
            return {
              ...newSpace,
              squareFootage:
                spaceAttributes.find((attr) => attr.code === 'square_footage')
                  ?.value || 'unknown',
              sizingAttributes: Object.fromEntries(
                Object.entries(newSpace.sizingAttributes || {})
                  .filter(([, value]) => value.length > 0)
                  .map(([key, value]) => [
                    key,
                    value.map((item) => filterSizingFields(item))
                  ])
              ),
              isTopFloor,
              isBottomFloor,
              isBasementLevel,
              isBelowAttic,
              isBelowRoof,
              isAboveCrawlspace,
              isAboveBasement,
              isBasementConditioned
            };
          })
        };
      })
    };
    const result = {
      ...newLevel,
      level: currentLevel
    };
    currentLevel -= 1;
    return result;
  });
};

export async function updateSpaceSizing(
  updateSpace: GeneratedSpaceType
): Promise<void> {
  if (!updateSpace.spaceId) {
    errorModal('Failed to update space sizing.');
    return;
  }

  const averageCeilingHeight = getProjectSettingValue('ceiling_height') ?? 8;

  const currentStructure = store.getState().structure.structure;
  const currentSpace = findSpaceById(currentStructure, updateSpace.spaceId);
  if (currentSpace) {
    const squareFootage = updateSpace.squareFootage || 0;

    const newSizingAttributes: SizingCalculatorAttributesType = {
      aboveGradeWalls: [],
      belowGradeWalls: [],
      ceilingsBelowRoof: [],
      ceilingsBelowAttic: [],
      floorsOverUnconditionedSpace: [],
      floorsOverExposedSpace: [],
      basementFloors: [],
      slabFloors: [],
      appliances: currentSpace.sizingAttributes?.appliances || []
    };

    if (updateSpace.ceilings && updateSpace.ceilings.length > 0) {
      updateSpace.ceilings.forEach((ceiling) => {
        if (ceiling.type === 'Below Attic') {
          newSizingAttributes.ceilingsBelowAttic.push({
            ...defaultCeilingBelowAttic,
            uniqueId: nanoid(5),
            valid: true,
            area: squareFootage
          });
        } else if (ceiling.type === 'Below Roof Joists') {
          newSizingAttributes.ceilingsBelowRoof.push({
            ...defaultCeilingBelowRoof,
            uniqueId: nanoid(5),
            valid: true,
            area: squareFootage
          });
        } else if (ceiling.type === 'On Exposed Beams') {
          newSizingAttributes.ceilingsBelowRoof.push({
            ...defaultCeilingOnExposedBeams,
            uniqueId: nanoid(5),
            valid: true,
            area: squareFootage
          });
        }
      });
    }

    if (updateSpace.walls && updateSpace.walls.length > 0) {
      updateSpace.walls.forEach((wall) => {
        if (wall.type === 'Above Grade') {
          newSizingAttributes.aboveGradeWalls.push({
            ...defaultAboveGradeWall,
            uniqueId: nanoid(5),
            valid: true,
            height: averageCeilingHeight,
            width: Math.ceil(Math.sqrt(squareFootage)),
            exposure: wall.exposureDirection,
            verticalGlass: wall.windows.map((window) => ({
              ...defaultGlass,
              uniqueId: nanoid(5),
              valid: true,
              height: Math.ceil(Math.sqrt(window.area)),
              width: Math.ceil(Math.sqrt(window.area))
            })),
            solidDoors: wall.doors.map((door) => ({
              ...defaultDoor,
              uniqueId: nanoid(5),
              valid: true,
              height: 7,
              width: Math.ceil(door.area / 7)
            }))
          });
        } else if (wall.type === 'Below Grade') {
          newSizingAttributes.belowGradeWalls.push({
            ...defaultBelowGradeWall,
            uniqueId: nanoid(5),
            valid: true,
            height: averageCeilingHeight,
            width: Math.ceil(Math.sqrt(squareFootage)),
            exposure: wall.exposureDirection,
            verticalGlass: wall.windows.map((window) => ({
              ...defaultGlass,
              uniqueId: nanoid(5),
              valid: true,
              height: Math.ceil(Math.sqrt(window.area)),
              width: Math.ceil(Math.sqrt(window.area))
            })),
            solidDoors: wall.doors.map((door) => ({
              ...defaultDoor,
              uniqueId: nanoid(5),
              valid: true,
              height: 7,
              width: Math.ceil(door.area / 7)
            }))
          });
        }
      });
    }

    if (updateSpace.floors && updateSpace.floors.length > 0) {
      updateSpace.floors.forEach((floor) => {
        if (floor.type === 'Above Unconditioned Space') {
          newSizingAttributes.floorsOverUnconditionedSpace.push({
            ...defaultFloorOverUnconditionedSpace,
            uniqueId: nanoid(5),
            valid: true,
            area: squareFootage
          });
        } else if (floor.type === 'Over Exposed Space') {
          newSizingAttributes.floorsOverExposedSpace.push({
            ...defaultFloorOverExposedSpace,
            uniqueId: nanoid(5),
            valid: true,
            area: squareFootage
          });
        } else if (floor.type === 'Basement Floor') {
          newSizingAttributes.basementFloors.push({
            ...defaultBasementFloor,
            uniqueId: nanoid(5),
            valid: true,
            area: squareFootage
          });
        } else if (floor.type === 'Slab Floor') {
          newSizingAttributes.slabFloors.push({
            ...defaultSlabFloor,
            uniqueId: nanoid(5),
            valid: true,
            exposedEdgeFeet: Math.sqrt(squareFootage) * 4
          });
        }
      });
    }

    const newStructure = produce(currentStructure, (draft) => {
      const space = findSpaceById(draft, currentSpace.uniqueId);
      if (space) {
        const { appliances, ...restSizingAttributes } =
          space.sizingAttributes || {};
        const hasSizingAttributes = Object.values(restSizingAttributes).some(
          (attr) => attr.length > 0
        );
        const hasSquareFootage = space.attributes.some(
          (attr) => attr.code === 'square_footage' && attr.value
        );
        if (!hasSquareFootage) {
          space.attributes = space.attributes.map((attr) =>
            attr.code === 'square_footage'
              ? { ...attr, value: squareFootage }
              : attr
          );
        }
        if (!hasSizingAttributes) {
          space.sizingAttributes = newSizingAttributes;
        }
      }
    });

    store.dispatch(setStructure(newStructure));
  } else {
    errorModal('Failed to update sizing items in structure.');
    console.error(`Space not found`);
  }
}

export function prepareLevelsForDatabase(
  originalLevels: LevelType[]
): LevelForDatabaseType[] {
  const { structureUniqueId } = store.getState().structure;

  if (!structureUniqueId) {
    console.error('Structure unique ID not found');
    errorModal('Structure unique ID not found');
    return [];
  }

  return originalLevels.map((level, levelIndex) => ({
    ...level,
    structureUniqueId,
    floorNum: levelIndex,
    attributes: truncateAttributes(level.attributes),
    sections: level.sections.map((section) => ({
      ...section,
      levelUniqueId: level.uniqueId,
      attributes: truncateAttributes(section.attributes),
      spaces: section.spaces.map((space) => ({
        ...space,
        sectionUniqueId: section.uniqueId,
        attributes: truncateAttributes(space.attributes)
      }))
    }))
  }));
}

export function revalidateStructureSizingAttributes(structure: LevelType[]) {
  return structure.map((level) => ({
    ...level,
    sections: level.sections.map((section) => ({
      ...section,
      spaces: section.spaces.map((space) => ({
        ...space,
        sizingAttributes: validateSizingAttributes(
          space.sizingAttributes || ({} as SizingCalculatorAttributesType)
        )
      }))
    }))
  }));
}

export function restoreStructureAttributes(
  truncatedStructure: TruncatedLevelType[]
) {
  return truncatedStructure.map((level) => ({
    ...level,
    attributes: restoreAttributes(level.attributes),
    sections: level.sections.map((section) => ({
      ...section,
      attributes: restoreAttributes(section.attributes),
      spaces: section.spaces.map((space) => ({
        ...space,
        attributes: restoreAttributes(space.attributes)
      }))
    }))
  })) as LevelType[];
}

export function getOtherSpaceOptions(spaceUniqueId: string, sameFloor = false) {
  const { structure } = store.getState().structure;

  if (sameFloor) {
    return (
      structure
        .find((l) =>
          l.sections.some((s) =>
            s.spaces.some((sp) => sp.uniqueId === spaceUniqueId)
          )
        )
        ?.sections.flatMap((s) => s.spaces)
        .filter((sp) => sp.uniqueId !== spaceUniqueId)
        .map((sp) => ({ label: sp.label, value: sp.uniqueId })) ?? []
    );
  }

  return structure.flatMap((l) =>
    l.sections
      .flatMap((s) => s.spaces)
      .filter((sp) => sp.uniqueId !== spaceUniqueId)
      .map((sp) => ({ label: sp.label, value: sp.uniqueId }))
  );
}

export function findClosestSpaceInSection(
  spacePos: PositionAndDimensionsType,
  otherSpaces: Array<{ id: string; position: PositionAndDimensionsType }>
) {
  if (otherSpaces.length === 0) return null;

  const spaceCenterX = spacePos.x + spacePos.width / 2;
  const spaceCenterY = spacePos.y + spacePos.height / 2;

  let closestSpace = otherSpaces[0];
  let minDistance = Infinity;

  otherSpaces.forEach((other) => {
    const otherCenterX = other.position.x + other.position.width / 2;
    const otherCenterY = other.position.y + other.position.height / 2;
    const distance = Math.sqrt(
      (spaceCenterX - otherCenterX) ** 2 + (spaceCenterY - otherCenterY) ** 2
    );

    if (distance < minDistance) {
      minDistance = distance;
      closestSpace = other;
    }
  });

  return closestSpace;
}

function buildOffsetsForDistance(
  distance: number
): Array<{ deltaX: number; deltaY: number }> {
  const offsets: Array<{ deltaX: number; deltaY: number }> = [
    { deltaX: distance, deltaY: 0 },
    { deltaX: -distance, deltaY: 0 },
    { deltaX: 0, deltaY: distance },
    { deltaX: 0, deltaY: -distance }
  ];

  for (let xDistance = distance - 1; xDistance >= 1; xDistance -= 1) {
    const yDistance = distance - xDistance;
    offsets.push(
      { deltaX: xDistance, deltaY: yDistance },
      { deltaX: xDistance, deltaY: -yDistance },
      { deltaX: -xDistance, deltaY: yDistance },
      { deltaX: -xDistance, deltaY: -yDistance }
    );
  }

  return offsets;
}

export function findMinimalMoveToAvoidOverlap(
  movingSpaces: PositionAndDimensionsType | PositionAndDimensionsType[],
  allOtherSpaces: PositionAndDimensionsType[]
): { deltaX: number; deltaY: number } {
  const spacesToMove = Array.isArray(movingSpaces)
    ? movingSpaces
    : [movingSpaces];

  const isOverlapping = (
    pos: PositionAndDimensionsType,
    space: PositionAndDimensionsType
  ): boolean => {
    return (
      pos.x < space.x + space.width &&
      pos.x + pos.width > space.x &&
      pos.y < space.y + space.height &&
      pos.y + pos.height > space.y
    );
  };

  const hasAnyOverlapWithDelta = (deltaX: number, deltaY: number): boolean => {
    return spacesToMove.some((movingSpace) => {
      const adjustedPos = {
        x: movingSpace.x + deltaX,
        y: movingSpace.y + deltaY,
        width: movingSpace.width,
        height: movingSpace.height
      };
      return allOtherSpaces.some((otherSpace) =>
        isOverlapping(adjustedPos, otherSpace)
      );
    });
  };

  if (!hasAnyOverlapWithDelta(0, 0)) {
    return { deltaX: 0, deltaY: 0 };
  }

  for (let distance = 1; distance <= 50; distance += 1) {
    const offsets = buildOffsetsForDistance(distance);
    for (const { deltaX, deltaY } of offsets) {
      if (!hasAnyOverlapWithDelta(deltaX, deltaY)) {
        return { deltaX, deltaY };
      }
    }
  }

  return { deltaX: 0, deltaY: 0 };
}

export type MinimalMoveConstraintFn = (
  movedSpaces: PositionAndDimensionsType[]
) => boolean;

export function findMinimalMoveToAvoidOverlapWithConstraint(
  movingSpaces: PositionAndDimensionsType | PositionAndDimensionsType[],
  allOtherSpaces: PositionAndDimensionsType[],
  constraint: MinimalMoveConstraintFn,
  maxDistance = 50
): { deltaX: number; deltaY: number } {
  const spacesToMove = Array.isArray(movingSpaces)
    ? movingSpaces
    : [movingSpaces];

  const isOverlapping = (
    pos: PositionAndDimensionsType,
    space: PositionAndDimensionsType
  ): boolean => {
    return (
      pos.x < space.x + space.width &&
      pos.x + pos.width > space.x &&
      pos.y < space.y + space.height &&
      pos.y + pos.height > space.y
    );
  };

  const movedSpacesWithDelta = (deltaX: number, deltaY: number) => {
    return spacesToMove.map((space) => ({
      x: space.x + deltaX,
      y: space.y + deltaY,
      width: space.width,
      height: space.height
    }));
  };

  const hasAnyOverlapWithDelta = (deltaX: number, deltaY: number): boolean => {
    const moved = movedSpacesWithDelta(deltaX, deltaY);
    return moved.some((pos) =>
      allOtherSpaces.some((otherSpace) => isOverlapping(pos, otherSpace))
    );
  };

  if (!hasAnyOverlapWithDelta(0, 0) && constraint(movedSpacesWithDelta(0, 0))) {
    return { deltaX: 0, deltaY: 0 };
  }

  for (let distance = 1; distance <= maxDistance; distance += 1) {
    const offsets = buildOffsetsForDistance(distance);
    for (const { deltaX, deltaY } of offsets) {
      if (
        !hasAnyOverlapWithDelta(deltaX, deltaY) &&
        constraint(movedSpacesWithDelta(deltaX, deltaY))
      ) {
        return { deltaX, deltaY };
      }
    }
  }

  return { deltaX: 0, deltaY: 0 };
}

export function findMinimalShiftAvoidingPolygons(
  rect: PositionAndDimensionsType,
  obstaclePolygons: PositionType[][],
  maxDistance = 50
): { deltaX: number; deltaY: number } | null {
  const overlapsAny = (deltaX: number, deltaY: number): boolean => {
    const movedPolygon = rectToAbsolutePolygon({
      x: rect.x + deltaX,
      y: rect.y + deltaY,
      width: rect.width,
      height: rect.height
    });
    return obstaclePolygons.some((p) => polygonsOverlapStrict(movedPolygon, p));
  };

  if (!overlapsAny(0, 0)) {
    return { deltaX: 0, deltaY: 0 };
  }

  for (let distance = 1; distance <= maxDistance; distance += 1) {
    const offsets = buildOffsetsForDistance(distance);
    for (const { deltaX, deltaY } of offsets) {
      if (!overlapsAny(deltaX, deltaY)) {
        return { deltaX, deltaY };
      }
    }
  }

  return null;
}

export function fitSpaceAmongOthers(
  droppedSpace: PositionAndDimensionsType,
  otherSpaces: PositionAndDimensionsType[],
  maxFallbackMoveDistance = 50,
  obstaclePolygons?: PositionType[][],
  footprintPolygons?: FootprintPolygonsType | null
): PositionAndDimensionsType | null {
  const snapped = {
    x: Math.round(droppedSpace.x),
    y: Math.round(droppedSpace.y),
    width: Math.round(droppedSpace.width),
    height: Math.round(droppedSpace.height)
  };
  const footprint = findContainingPolygon(
    snapped,
    (footprintPolygons ?? []).filter((polygon) => polygon.length >= 3)
  );

  const isOverlapping = (
    a: PositionAndDimensionsType,
    b: PositionAndDimensionsType
  ): boolean =>
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y;

  const hasFootprint = !!footprint && footprint.length >= 3;
  const usePolygons =
    !!obstaclePolygons &&
    obstaclePolygons.length > 0 &&
    obstaclePolygons.length === otherSpaces.length;

  const obstaclesOverlapRect = (rect: PositionAndDimensionsType): boolean => {
    if (usePolygons) {
      return polygonsOverlapRect(rect, obstaclePolygons!);
    }
    return otherSpaces.some((s) => isOverlapping(rect, s));
  };

  const isPlacementValid = (rect: PositionAndDimensionsType): boolean => {
    if (obstaclesOverlapRect(rect)) return false;
    if (hasFootprint && !isRectInPolygon(rect, footprint!)) return false;
    return true;
  };

  const clampedSnapped = hasFootprint
    ? clampRectToFootprint(snapped, footprint!)
    : snapped;

  if (isPlacementValid(clampedSnapped)) {
    return clampedSnapped;
  }

  if (hasFootprint && usePolygons) {
    const openAreaFit = findOpenAreaFit(
      clampedSnapped,
      obstaclePolygons!,
      footprint!
    );
    if (openAreaFit && isPlacementValid(openAreaFit)) {
      return openAreaFit;
    }
  }

  const getOverlappingPolygonIndices = (
    rect: PositionAndDimensionsType
  ): number[] => {
    const result: number[] = [];
    if (!usePolygons) return result;
    const rp = rectToAbsolutePolygon(rect);
    for (let i = 0; i < obstaclePolygons!.length; i += 1) {
      const poly = obstaclePolygons![i];
      if (poly.length >= 3 && polygonsOverlapStrict(rp, poly)) {
        result.push(i);
      }
    }
    return result;
  };

  let overlapping: PositionAndDimensionsType[] = [];
  let overlappingPolyIndices: number[] = [];
  if (usePolygons) {
    overlappingPolyIndices = getOverlappingPolygonIndices(clampedSnapped);
  } else {
    overlapping = otherSpaces.filter((s) => isOverlapping(clampedSnapped, s));
  }

  const centerX = clampedSnapped.x + clampedSnapped.width / 2;
  const centerY = clampedSnapped.y + clampedSnapped.height / 2;

  const tryHorizontalFit = (): PositionAndDimensionsType | null => {
    let left = clampedSnapped.x;
    let right = clampedSnapped.x + clampedSnapped.width;
    const y0 = clampedSnapped.y;
    const y1 = clampedSnapped.y + clampedSnapped.height;

    if (usePolygons) {
      for (let k = 0; k < overlappingPolyIndices.length; k += 1) {
        const poly = obstaclePolygons![overlappingPolyIndices[k]];
        const r = applyPolygonHorizontalShrink(
          poly,
          y0,
          y1,
          centerX,
          left,
          right
        );
        left = r.left;
        right = r.right;
      }
    } else {
      for (let o = 0; o < overlapping.length; o += 1) {
        const other = overlapping[o];
        const otherCenterX = other.x + other.width / 2;
        if (otherCenterX < centerX) {
          left = Math.max(left, other.x + other.width);
        } else {
          right = Math.min(right, other.x);
        }
      }
    }

    const w = right - left;
    if (w < 1) return null;

    return {
      x: left,
      y: clampedSnapped.y,
      width: w,
      height: clampedSnapped.height
    };
  };

  const tryVerticalFit = (): PositionAndDimensionsType | null => {
    let top = clampedSnapped.y;
    let bottom = clampedSnapped.y + clampedSnapped.height;
    const x0 = clampedSnapped.x;
    const x1 = clampedSnapped.x + clampedSnapped.width;

    if (usePolygons) {
      for (let k = 0; k < overlappingPolyIndices.length; k += 1) {
        const poly = obstaclePolygons![overlappingPolyIndices[k]];
        const r = applyPolygonVerticalShrink(
          poly,
          x0,
          x1,
          centerY,
          top,
          bottom
        );
        top = r.top;
        bottom = r.bottom;
      }
    } else {
      for (let o = 0; o < overlapping.length; o += 1) {
        const other = overlapping[o];
        const otherCenterY = other.y + other.height / 2;
        if (otherCenterY < centerY) {
          top = Math.max(top, other.y + other.height);
        } else {
          bottom = Math.min(bottom, other.y);
        }
      }
    }

    const h = bottom - top;
    if (h < 1) return null;

    return {
      x: clampedSnapped.x,
      y: top,
      width: clampedSnapped.width,
      height: h
    };
  };

  const hResult = tryHorizontalFit();
  const vResult = tryVerticalFit();

  const hValid = hResult !== null && isPlacementValid(hResult);
  const vValid = vResult !== null && isPlacementValid(vResult);

  if (hValid && vValid) {
    const hArea = hResult.width * hResult.height;
    const vArea = vResult!.width * vResult!.height;
    return hArea >= vArea ? hResult : vResult!;
  }
  if (hValid) return hResult!;
  if (vValid) return vResult!;

  const tryCombinedFit = (
    first: PositionAndDimensionsType | null,
    secondAxis: 'horizontal' | 'vertical'
  ): PositionAndDimensionsType | null => {
    if (!first) return null;

    if (usePolygons) {
      const firstPolyIdx = getOverlappingPolygonIndices(first);
      if (firstPolyIdx.length === 0) return first;
      const fc = {
        x: first.x + first.width / 2,
        y: first.y + first.height / 2
      };

      if (secondAxis === 'vertical') {
        let top = first.y;
        let bottom = first.y + first.height;
        for (let k = 0; k < firstPolyIdx.length; k += 1) {
          const poly = obstaclePolygons![firstPolyIdx[k]];
          const r = applyPolygonVerticalShrink(
            poly,
            first.x,
            first.x + first.width,
            fc.y,
            top,
            bottom
          );
          top = r.top;
          bottom = r.bottom;
        }
        const h = bottom - top;
        if (h < 1) return null;
        return {
          x: first.x,
          y: top,
          width: first.width,
          height: h
        };
      }

      let left = first.x;
      let right = first.x + first.width;
      for (let k = 0; k < firstPolyIdx.length; k += 1) {
        const poly = obstaclePolygons![firstPolyIdx[k]];
        const r = applyPolygonHorizontalShrink(
          poly,
          first.y,
          first.y + first.height,
          fc.x,
          left,
          right
        );
        left = r.left;
        right = r.right;
      }
      const w = right - left;
      if (w < 1) return null;
      return {
        x: left,
        y: first.y,
        width: w,
        height: first.height
      };
    }

    const firstOverlapping = otherSpaces.filter((s) => isOverlapping(first, s));
    if (firstOverlapping.length === 0) return first;
    const fc = {
      x: first.x + first.width / 2,
      y: first.y + first.height / 2
    };

    if (secondAxis === 'vertical') {
      let top = first.y;
      let bottom = first.y + first.height;
      for (const other of firstOverlapping) {
        if (other.y + other.height / 2 < fc.y) {
          top = Math.max(top, other.y + other.height);
        } else {
          bottom = Math.min(bottom, other.y);
        }
      }
      const h = bottom - top;
      if (h < 1) return null;
      return {
        x: first.x,
        y: top,
        width: first.width,
        height: h
      };
    }

    let left = first.x;
    let right = first.x + first.width;
    for (const other of firstOverlapping) {
      if (other.x + other.width / 2 < fc.x) {
        left = Math.max(left, other.x + other.width);
      } else {
        right = Math.min(right, other.x);
      }
    }
    const w = right - left;
    if (w < 1) return null;
    return {
      x: left,
      y: first.y,
      width: w,
      height: first.height
    };
  };

  const hvResult = tryCombinedFit(hResult, 'vertical');
  const vhResult = tryCombinedFit(vResult, 'horizontal');

  const hvValid = hvResult !== null && isPlacementValid(hvResult);
  const vhValid = vhResult !== null && isPlacementValid(vhResult);

  if (hvValid && vhValid) {
    const hvArea = hvResult!.width * hvResult!.height;
    const vhArea = vhResult!.width * vhResult!.height;
    return hvArea >= vhArea ? hvResult! : vhResult!;
  }
  if (hvValid) return hvResult!;
  if (vhValid) return vhResult!;

  const tryCenterBasedFit = (): PositionAndDimensionsType | null => {
    if (usePolygons) {
      for (let i = 0; i < obstaclePolygons!.length; i += 1) {
        const poly = obstaclePolygons![i];
        if (
          poly.length >= 3 &&
          isPointInPolygon({ x: centerX, y: centerY }, poly)
        ) {
          return null;
        }
      }

      let left = clampedSnapped.x;
      let right = clampedSnapped.x + clampedSnapped.width;
      let top = clampedSnapped.y;
      let bottom = clampedSnapped.y + clampedSnapped.height;

      for (let i = 0; i < obstaclePolygons!.length; i += 1) {
        const poly = obstaclePolygons![i];
        if (poly.length >= 3) {
          const overlapsRect = polygonsOverlapStrict(
            rectToAbsolutePolygon({
              x: left,
              y: top,
              width: right - left,
              height: bottom - top
            }),
            poly
          );

          if (overlapsRect) {
            const bb = polygonBoundingBox(poly);
            const oR = bb.maxX;
            const oB = bb.maxY;

            const candidates: {
              left: number;
              right: number;
              top: number;
              bottom: number;
            }[] = [];

            if (oR > left && oR <= centerX)
              candidates.push({ left: oR, right, top, bottom });
            if (bb.minX < right && bb.minX >= centerX)
              candidates.push({ left, right: bb.minX, top, bottom });
            if (oB > top && oB <= centerY)
              candidates.push({ left, right, top: oB, bottom });
            if (bb.minY < bottom && bb.minY >= centerY)
              candidates.push({ left, right, top, bottom: bb.minY });

            if (candidates.length === 0) return null;

            let best = candidates[0];
            let bestArea = (best.right - best.left) * (best.bottom - best.top);
            for (let c = 1; c < candidates.length; c += 1) {
              const cand = candidates[c];
              const area = (cand.right - cand.left) * (cand.bottom - cand.top);
              if (area > bestArea) {
                best = cand;
                bestArea = area;
              }
            }

            left = best.left;
            right = best.right;
            top = best.top;
            bottom = best.bottom;
          }
        }
      }

      const w = right - left;
      const h = bottom - top;
      if (w < 3 || h < 3) return null;

      return {
        x: left,
        y: top,
        width: w,
        height: h
      };
    }

    if (
      otherSpaces.some(
        (s) =>
          centerX > s.x &&
          centerX < s.x + s.width &&
          centerY > s.y &&
          centerY < s.y + s.height
      )
    ) {
      return null;
    }

    let left = clampedSnapped.x;
    let right = clampedSnapped.x + clampedSnapped.width;
    let top = clampedSnapped.y;
    let bottom = clampedSnapped.y + clampedSnapped.height;

    for (const other of otherSpaces) {
      const oR = other.x + other.width;
      const oB = other.y + other.height;

      const overlapsRect =
        other.x < right && oR > left && other.y < bottom && oB > top;

      if (overlapsRect) {
        const candidates: {
          left: number;
          right: number;
          top: number;
          bottom: number;
        }[] = [];

        if (oR > left && oR <= centerX)
          candidates.push({ left: oR, right, top, bottom });
        if (other.x < right && other.x >= centerX)
          candidates.push({ left, right: other.x, top, bottom });
        if (oB > top && oB <= centerY)
          candidates.push({ left, right, top: oB, bottom });
        if (other.y < bottom && other.y >= centerY)
          candidates.push({ left, right, top, bottom: other.y });

        if (candidates.length === 0) return null;

        let best = candidates[0];
        let bestArea = (best.right - best.left) * (best.bottom - best.top);
        for (let i = 1; i < candidates.length; i += 1) {
          const c = candidates[i];
          const area = (c.right - c.left) * (c.bottom - c.top);
          if (area > bestArea) {
            best = c;
            bestArea = area;
          }
        }

        left = best.left;
        right = best.right;
        top = best.top;
        bottom = best.bottom;
      }
    }

    const w = right - left;
    const h = bottom - top;
    if (w < 3 || h < 3) return null;

    return {
      x: left,
      y: top,
      width: w,
      height: h
    };
  };

  const centerResult = tryCenterBasedFit();
  if (centerResult !== null && isPlacementValid(centerResult)) {
    return centerResult;
  }

  if (obstaclePolygons) {
    const polygonShift = findMinimalShiftAvoidingPolygons(
      clampedSnapped,
      obstaclePolygons,
      maxFallbackMoveDistance
    );
    if (polygonShift) {
      const polygonShifted = {
        ...clampedSnapped,
        x: clampedSnapped.x + polygonShift.deltaX,
        y: clampedSnapped.y + polygonShift.deltaY
      };
      if (isPlacementValid(polygonShifted)) {
        return polygonShifted;
      }
    }
  }

  const shiftAdjustment = findMinimalMoveToAvoidOverlapWithConstraint(
    clampedSnapped,
    otherSpaces,
    () => true,
    maxFallbackMoveDistance
  );
  const shifted = {
    ...clampedSnapped,
    x: clampedSnapped.x + shiftAdjustment.deltaX,
    y: clampedSnapped.y + shiftAdjustment.deltaY
  };
  if (isPlacementValid(shifted)) {
    return shifted;
  }

  return null;
}

export type SpaceWithId = PositionAndDimensionsType & { spaceId: string };

export type ShiftResult = {
  shifts: Map<string, { x: number; y: number }>;
  wouldCascade: boolean;
};

export function shiftOverlappingSpaces(
  droppedSpace: PositionAndDimensionsType,
  allOtherSpaces: SpaceWithId[]
): ShiftResult {
  const shifts = new Map<string, { x: number; y: number }>();

  const isOverlapping = (
    pos1: PositionAndDimensionsType,
    pos2: PositionAndDimensionsType
  ): boolean => {
    return (
      pos1.x < pos2.x + pos2.width &&
      pos1.x + pos1.width > pos2.x &&
      pos1.y < pos2.y + pos2.height &&
      pos1.y + pos1.height > pos2.y
    );
  };

  const overlappingSpaces = allOtherSpaces.filter((space) =>
    isOverlapping(droppedSpace, space)
  );

  if (overlappingSpaces.length === 0) {
    return { shifts, wouldCascade: false };
  }

  const overlappingSpaceIds = new Set(overlappingSpaces.map((s) => s.spaceId));
  const nonOverlappingSpaces = allOtherSpaces.filter(
    (space) => !overlappingSpaceIds.has(space.spaceId)
  );

  for (const overlappingSpace of overlappingSpaces) {
    const droppedCenterX = droppedSpace.x + droppedSpace.width / 2;
    const droppedCenterY = droppedSpace.y + droppedSpace.height / 2;
    const spaceCenterX = overlappingSpace.x + overlappingSpace.width / 2;
    const spaceCenterY = overlappingSpace.y + overlappingSpace.height / 2;

    const overlapLeft = Math.max(droppedSpace.x, overlappingSpace.x);
    const overlapRight = Math.min(
      droppedSpace.x + droppedSpace.width,
      overlappingSpace.x + overlappingSpace.width
    );
    const overlapTop = Math.max(droppedSpace.y, overlappingSpace.y);
    const overlapBottom = Math.min(
      droppedSpace.y + droppedSpace.height,
      overlappingSpace.y + overlappingSpace.height
    );
    const overlapWidth = overlapRight - overlapLeft;
    const overlapHeight = overlapBottom - overlapTop;

    let deltaX = 0;
    let deltaY = 0;

    if (overlapWidth <= overlapHeight) {
      if (spaceCenterX >= droppedCenterX) {
        deltaX = droppedSpace.x + droppedSpace.width - overlappingSpace.x;
      } else {
        deltaX = droppedSpace.x - (overlappingSpace.x + overlappingSpace.width);
      }
    } else if (spaceCenterY >= droppedCenterY) {
      deltaY = droppedSpace.y + droppedSpace.height - overlappingSpace.y;
    } else {
      deltaY = droppedSpace.y - (overlappingSpace.y + overlappingSpace.height);
    }

    const newX = overlappingSpace.x + deltaX;
    const newY = overlappingSpace.y + deltaY;

    shifts.set(overlappingSpace.spaceId, { x: newX, y: newY });
  }

  let wouldCascade = false;
  shifts.forEach((newPosition, spaceId) => {
    const originalSpace = overlappingSpaces.find((s) => s.spaceId === spaceId);
    if (!originalSpace) return;

    const shiftedSpaceBounds: PositionAndDimensionsType = {
      x: newPosition.x,
      y: newPosition.y,
      width: originalSpace.width,
      height: originalSpace.height
    };

    for (const nonOverlapping of nonOverlappingSpaces) {
      if (isOverlapping(shiftedSpaceBounds, nonOverlapping)) {
        wouldCascade = true;
        return;
      }
    }
  });

  return { shifts, wouldCascade };
}

export function findAdjacentPosition(
  targetSpacePos: PositionAndDimensionsType,
  movingSpacePos: PositionAndDimensionsType,
  allSpaces: PositionAndDimensionsType[]
): PositionAndDimensionsType {
  const isOverlapping = (
    pos: PositionAndDimensionsType,
    spaces: PositionAndDimensionsType[]
  ) => {
    return spaces.some(
      (space) =>
        pos.x < space.x + space.width &&
        pos.x + pos.width > space.x &&
        pos.y < space.y + space.height &&
        pos.y + pos.height > space.y
    );
  };

  const calculateDistance = (pos: PositionAndDimensionsType) => {
    const centerX1 = movingSpacePos.x + movingSpacePos.width / 2;
    const centerY1 = movingSpacePos.y + movingSpacePos.height / 2;
    const centerX2 = pos.x + pos.width / 2;
    const centerY2 = pos.y + pos.height / 2;
    return Math.sqrt((centerX2 - centerX1) ** 2 + (centerY2 - centerY1) ** 2);
  };

  const possiblePositions: Array<{
    pos: PositionAndDimensionsType;
    distance: number;
  }> = [];

  const addHorizontalPositions = (y: number) => {
    const rightPos = {
      x: targetSpacePos.x + targetSpacePos.width,
      y,
      width: movingSpacePos.width,
      height: movingSpacePos.height
    };
    if (!isOverlapping(rightPos, allSpaces)) {
      possiblePositions.push({
        pos: rightPos,
        distance: calculateDistance(rightPos)
      });
    }

    const leftPos = {
      x: targetSpacePos.x - movingSpacePos.width,
      y,
      width: movingSpacePos.width,
      height: movingSpacePos.height
    };
    if (!isOverlapping(leftPos, allSpaces)) {
      possiblePositions.push({
        pos: leftPos,
        distance: calculateDistance(leftPos)
      });
    }
  };

  const addVerticalPositions = (x: number) => {
    const bottomPos = {
      x,
      y: targetSpacePos.y + targetSpacePos.height,
      width: movingSpacePos.width,
      height: movingSpacePos.height
    };
    if (!isOverlapping(bottomPos, allSpaces)) {
      possiblePositions.push({
        pos: bottomPos,
        distance: calculateDistance(bottomPos)
      });
    }

    const topPos = {
      x,
      y: targetSpacePos.y - movingSpacePos.height,
      width: movingSpacePos.width,
      height: movingSpacePos.height
    };
    if (!isOverlapping(topPos, allSpaces)) {
      possiblePositions.push({
        pos: topPos,
        distance: calculateDistance(topPos)
      });
    }
  };

  addHorizontalPositions(movingSpacePos.y);
  addVerticalPositions(movingSpacePos.x);

  if (possiblePositions.length > 0) {
    possiblePositions.sort((a, b) => a.distance - b.distance);
    return possiblePositions[0].pos;
  }

  const fallbackPos = {
    x: targetSpacePos.x + targetSpacePos.width,
    y: movingSpacePos.y,
    width: movingSpacePos.width,
    height: movingSpacePos.height
  };
  return fallbackPos;
}

export function shouldShowSpaceLabel<T extends SpacePositionData>(
  space: T,
  allSpaces: T[],
  structure: LevelType[],
  showLabel = true
): boolean {
  const currentSpace = findSpaceById(structure, space.spaceId);
  const hasOpenTo = (currentSpace?.openTo?.length ?? 0) > 0;

  if (!hasOpenTo) {
    return showLabel;
  }

  if (!space.sectionId || space.spacesInSection === 1) {
    return showLabel;
  }

  const spacesInSection = allSpaces.filter(
    (s) => s.sectionId === space.sectionId
  );

  const spacesWithSameLabel = spacesInSection.filter(
    (s) => s.label === space.label
  );

  if (spacesWithSameLabel.length <= 1) {
    return showLabel;
  }

  const otherOpenSpacesWithSameLabel = spacesWithSameLabel.filter((s) => {
    if (s.id === space.id) return false;
    const otherSpace = findSpaceById(structure, s.spaceId);
    return (otherSpace?.openTo?.length ?? 0) > 0;
  });

  if (otherOpenSpacesWithSameLabel.length === 0) {
    return showLabel;
  }

  const openSpacesWithSameLabel = spacesWithSameLabel.filter((s) => {
    const spaceData = findSpaceById(structure, s.spaceId);
    return (spaceData?.openTo?.length ?? 0) > 0;
  });

  const largestArea = Math.max(
    ...openSpacesWithSameLabel.map((s) => s.position.width * s.position.height)
  );

  const largestSpaces = openSpacesWithSameLabel.filter(
    (s) => s.position.width * s.position.height === largestArea
  );

  if (largestSpaces.length === 1) {
    return largestSpaces[0].id === space.id && showLabel;
  }

  largestSpaces.sort((a, b) => {
    if (a.position.y !== b.position.y) {
      return a.position.y - b.position.y;
    }
    return a.position.x - b.position.x;
  });

  return largestSpaces[0].id === space.id && showLabel;
}

export function getEdgeItemsFromSpace(
  structure: LevelType[],
  spaceId: string,
  structureOrientation?: ExposureDirectionType
): EdgeItemsType {
  const edgeItems: EdgeItemsType = {
    top: [],
    right: [],
    bottom: [],
    left: []
  };

  const structureSpace = findSpaceById(structure, spaceId);

  if (!structureSpace?.sizingAttributes) {
    return edgeItems;
  }

  const { sizingAttributes } = structureSpace;

  sizingAttributes.aboveGradeWalls.forEach((wall) => {
    const edge =
      wall.edge || getOrientedEdge(wall.exposure, structureOrientation);
    if (!edge) return;

    wall.verticalGlass?.forEach((glass) => {
      const isGlassDoor =
        glass.fenestrationType === 'Sliding Glass Door' ||
        glass.fenestrationType === 'French Door';
      edgeItems[edge].push({
        type: isGlassDoor ? 'glass-door' : 'window',
        id: glass.uniqueId,
        width: glass.width,
        height: glass.height,
        valid: glass.valid,
        positionOnWall: glass.positionOnWall,
        ...(isGlassDoor
          ? {
              glassPaneCount: glass.fenestrationType === 'French Door' ? 2 : 1
            }
          : {})
      });
    });
  });

  sizingAttributes.belowGradeWalls.forEach((wall) => {
    const edge = getOrientedEdge(wall.exposure, structureOrientation);
    if (!edge) return;

    wall.verticalGlass?.forEach((glass) => {
      const isGlassDoor =
        glass.fenestrationType === 'Sliding Glass Door' ||
        glass.fenestrationType === 'French Door';
      edgeItems[edge].push({
        type: isGlassDoor ? 'glass-door' : 'window',
        id: glass.uniqueId,
        width: glass.width,
        height: glass.height,
        valid: glass.valid,
        positionOnWall: glass.positionOnWall,
        ...(isGlassDoor
          ? {
              glassPaneCount: glass.fenestrationType === 'French Door' ? 2 : 1
            }
          : {})
      });
    });
  });

  sizingAttributes.aboveGradeWalls.forEach((wall) => {
    const edge =
      wall.edge || getOrientedEdge(wall.exposure, structureOrientation);

    if (!edge) {
      return;
    }

    wall.solidDoors?.forEach((door) => {
      edgeItems[edge].push({
        type: 'door',
        id: door.uniqueId,
        width: door.width,
        height: door.height,
        material: door.material,
        valid: door.valid,
        positionOnWall: door.positionOnWall
      });
    });
  });

  sizingAttributes.belowGradeWalls.forEach((wall) => {
    const edge = getOrientedEdge(wall.exposure, structureOrientation);

    if (!edge) {
      return;
    }

    wall.solidDoors?.forEach((door) => {
      edgeItems[edge].push({
        type: 'door',
        id: door.uniqueId,
        width: door.width,
        height: door.height,
        material: door.material,
        valid: door.valid,
        positionOnWall: door.positionOnWall
      });
    });
  });

  return edgeItems;
}

export function findWallByFenestrationId(
  sizingAttributes: SizingCalculatorAttributesType | undefined,
  fenestrationId: string
): AboveGradeWallType | BelowGradeWallType | null {
  if (!sizingAttributes) return null;

  const aboveGradeMatch = sizingAttributes.aboveGradeWalls.find(
    (wall) =>
      wall.verticalGlass?.some((glass) => glass.uniqueId === fenestrationId) ||
      wall.solidDoors?.some((door) => door.uniqueId === fenestrationId)
  );
  if (aboveGradeMatch) return aboveGradeMatch;

  const belowGradeMatch = sizingAttributes.belowGradeWalls.find(
    (wall) =>
      wall.verticalGlass?.some((glass) => glass.uniqueId === fenestrationId) ||
      wall.solidDoors?.some((door) => door.uniqueId === fenestrationId)
  );
  return belowGradeMatch ?? null;
}

export function getAdjacentSpacesInSameSection<T extends SpacePositionData>(
  space: T,
  allSpaces: T[]
): { top: T | null; right: T | null; bottom: T | null; left: T | null } {
  if (!space.sectionId || !allSpaces.length) {
    return { top: null, right: null, bottom: null, left: null };
  }

  const sameSection = allSpaces.filter(
    (s) => s.sectionId === space.sectionId && s.id !== space.id
  );

  const adjacent = {
    top: null as T | null,
    right: null as T | null,
    bottom: null as T | null,
    left: null as T | null
  };

  sameSection.forEach((otherSpace) => {
    const currentPos = space.position;
    const otherPos = otherSpace.position;

    if (
      currentPos.x < otherPos.x + otherPos.width &&
      currentPos.x + currentPos.width > otherPos.x
    ) {
      if (currentPos.y === otherPos.y + otherPos.height) {
        adjacent.top = otherSpace;
      }
      if (currentPos.y + currentPos.height === otherPos.y) {
        adjacent.bottom = otherSpace;
      }
    }

    if (
      currentPos.y < otherPos.y + otherPos.height &&
      currentPos.y + currentPos.height > otherPos.y
    ) {
      if (currentPos.x === otherPos.x + otherPos.width) {
        adjacent.left = otherSpace;
      }
      if (currentPos.x + currentPos.width === otherPos.x) {
        adjacent.right = otherSpace;
      }
    }
  });

  return adjacent;
}

export function getWallSegments<T extends SpacePositionData>(
  space: T,
  allSpaces: T[],
  structure: LevelType[],
  direction: EdgeType
): Array<{ start: number; end: number }> {
  const tolerance = 0.1;

  const getAdjacentInDirection = (): T[] => {
    if (!allSpaces.length) {
      return [];
    }

    return allSpaces.filter((otherSpace) => {
      if (otherSpace.id === space.id) return false;
      const currentPos = space.position;
      const otherPos = otherSpace.position;

      switch (direction) {
        case 'top':
          return (
            currentPos.x < otherPos.x + otherPos.width &&
            currentPos.x + currentPos.width > otherPos.x &&
            Math.abs(currentPos.y - (otherPos.y + otherPos.height)) < tolerance
          );
        case 'bottom':
          return (
            currentPos.x < otherPos.x + otherPos.width &&
            currentPos.x + currentPos.width > otherPos.x &&
            Math.abs(currentPos.y + currentPos.height - otherPos.y) < tolerance
          );
        case 'left':
          return (
            currentPos.y < otherPos.y + otherPos.height &&
            currentPos.y + currentPos.height > otherPos.y &&
            Math.abs(currentPos.x - (otherPos.x + otherPos.width)) < tolerance
          );
        case 'right':
          return (
            currentPos.y < otherPos.y + otherPos.height &&
            currentPos.y + currentPos.height > otherPos.y &&
            Math.abs(currentPos.x + currentPos.width - otherPos.x) < tolerance
          );
        default:
          return false;
      }
    });
  };

  const adjacentSpaces = getAdjacentInDirection();

  if (adjacentSpaces.length === 0) {
    return [{ start: 0, end: 1 }];
  }

  const currentSpace = findSpaceById(structure, space.spaceId);

  const openToAdjacentSpaces = adjacentSpaces.filter((adjacentSpace) => {
    return currentSpace?.openTo?.includes(adjacentSpace.spaceId) ?? false;
  });

  if (openToAdjacentSpaces.length === 0) {
    return [{ start: 0, end: 1 }];
  }

  const currentPos = space.position;
  const hiddenRanges: Array<{ start: number; end: number }> = [];
  const isHorizontal = direction === 'top' || direction === 'bottom';

  openToAdjacentSpaces.forEach((adjacentSpace) => {
    const otherPos = adjacentSpace.position;

    if (isHorizontal) {
      const overlapStart = Math.max(currentPos.x, otherPos.x);
      const overlapEnd = Math.min(
        currentPos.x + currentPos.width,
        otherPos.x + otherPos.width
      );
      hiddenRanges.push({
        start: (overlapStart - currentPos.x) / currentPos.width,
        end: (overlapEnd - currentPos.x) / currentPos.width
      });
    } else {
      const overlapStart = Math.max(currentPos.y, otherPos.y);
      const overlapEnd = Math.min(
        currentPos.y + currentPos.height,
        otherPos.y + otherPos.height
      );
      hiddenRanges.push({
        start: (overlapStart - currentPos.y) / currentPos.height,
        end: (overlapEnd - currentPos.y) / currentPos.height
      });
    }
  });

  hiddenRanges.sort((a, b) => a.start - b.start);

  const mergedHiddenRanges: Array<{ start: number; end: number }> = [];
  let current = hiddenRanges[0];

  for (let i = 1; i < hiddenRanges.length; i += 1) {
    if (hiddenRanges[i].start <= current.end + 0.01) {
      current.end = Math.max(current.end, hiddenRanges[i].end);
    } else {
      mergedHiddenRanges.push(current);
      current = hiddenRanges[i];
    }
  }
  mergedHiddenRanges.push(current);

  const visibleSegments: Array<{ start: number; end: number }> = [];
  let lastEnd = 0;

  for (const hidden of mergedHiddenRanges) {
    if (hidden.start > lastEnd + 0.01) {
      visibleSegments.push({ start: lastEnd, end: hidden.start });
    }
    lastEnd = hidden.end;
  }

  if (lastEnd < 0.99) {
    visibleSegments.push({ start: lastEnd, end: 1 });
  }

  return visibleSegments.length > 0 ? visibleSegments : [];
}

export function isLevelType(
  item: LevelType | SectionType | SpaceType | null | undefined
): item is LevelType {
  return (
    !!item && 'sections' in item && Array.isArray((item as LevelType).sections)
  );
}

function isSectionType(
  item: LevelType | SectionType | SpaceType | null | undefined
): item is SectionType {
  return (
    !!item &&
    !isLevelType(item) &&
    'spaces' in item &&
    Array.isArray((item as SectionType).spaces)
  );
}

export function isSpaceType(
  item: LevelType | SectionType | SpaceType | null | undefined
): item is SpaceType {
  return (
    !!item &&
    !isLevelType(item) &&
    !isSectionType(item) &&
    'conditioned' in item
  );
}

export function levelHasDeclinedHeatingAndCooling(level: LevelType): boolean {
  const heating = level.attributes.find((a) => a.code === 'heating');
  const cooling = level.attributes.find((a) => a.code === 'cooling');
  return heating?.value === 'None' && cooling?.value === 'None';
}

function attributeHasValue(attr: AttributeType | undefined): boolean {
  if (!attr) return false;
  const v = attr.value;
  return v !== undefined && v !== null && v !== '';
}

export function levelHasRequiredLevelDetailsAttributes(
  level: LevelType
): boolean {
  if (level.conditioned === undefined) {
    return false;
  }
  const heating = level.attributes.find((a) => a.code === 'heating');
  const cooling = level.attributes.find((a) => a.code === 'cooling');
  const ceilingHeight = level.attributes.find(
    (a) => a.code === 'ceiling_height'
  );
  const levelCeilingType = level.attributes.find(
    (a) => a.code === 'level_ceiling_type'
  );
  const slopedHeight = level.attributes.find((a) => a.code === 'sloped_height');
  const vaultedHeight = level.attributes.find(
    (a) => a.code === 'vaulted_height'
  );
  const ductwork = level.attributes.find((a) => a.code === 'level_ductwork');
  if (heating && !attributeHasValue(heating)) return false;
  if (cooling && !attributeHasValue(cooling)) return false;
  if (isDuctedPathway() && ductwork && !attributeHasValue(ductwork)) {
    return false;
  }
  if (
    isLevelConditioned(level) &&
    ceilingHeight &&
    !attributeHasValue(ceilingHeight)
  ) {
    return false;
  }
  if (
    level.type === 'crawlspace' &&
    ceilingHeight &&
    !attributeHasValue(ceilingHeight)
  ) {
    return false;
  }
  if (level.type === 'level' && isLevelConditioned(level) && levelCeilingType) {
    if (!attributeHasValue(levelCeilingType)) return false;
    if (
      levelCeilingType.value === 'Sloped' &&
      slopedHeight &&
      !attributeHasValue(slopedHeight)
    ) {
      return false;
    }
    if (
      levelCeilingType.value === 'Vaulted' &&
      vaultedHeight &&
      !attributeHasValue(vaultedHeight)
    ) {
      return false;
    }
  }
  return true;
}

export function structureHasFlatCeilingLevel(structure: LevelType[]): boolean {
  return structure.some((level) =>
    level.attributes.some(
      (a) => a.code === 'level_ceiling_type' && a.value === 'Flat'
    )
  );
}

const BASEMENT_LEVEL_LABELS = new Set(['Basement', 'Crawlspace']);

export const getLowestNonBasementLevelIndex = (
  structure: LevelType[]
): number => {
  const index = structure.findIndex(
    (level) => !BASEMENT_LEVEL_LABELS.has(level.label)
  );
  return index === -1 ? 0 : index;
};

export function getAssignedImageForLevel(
  levelUniqueId: string,
  structureImageAssignments: Record<
    number,
    StructureImageAssignmentType | null
  >,
  structureAttachments: ProjectAttachmentType[]
): ProjectAttachmentType | null {
  const assignedEntry = Object.entries(structureImageAssignments).find(
    ([, assignment]) =>
      assignment?.structureItemUniqueId === levelUniqueId &&
      assignment?.structureItemTypeCode === 'level'
  );

  if (!assignedEntry) {
    return null;
  }

  const attachmentId = Number(assignedEntry[0]);
  return structureAttachments.find((a) => a.id === attachmentId) ?? null;
}

type StructureTypeWithSubtypes = {
  id: number;
  subtypes: { id: number; label: string }[];
};

type GetStructureDisplayNameParams = {
  structureName: string | null;
  structureTypeId: number | null;
  structureSubtypeId: number | null;
  structureTypes: StructureTypeWithSubtypes[];
};

export function getStructureDisplayName({
  structureName,
  structureTypeId,
  structureSubtypeId,
  structureTypes
}: GetStructureDisplayNameParams): string | null {
  if (structureName && structureName !== 'New Structure') {
    return structureName;
  }
  if (structureTypeId !== null && structureSubtypeId !== null) {
    const subtypeLabel = structureTypes
      .find((t) => t.id === structureTypeId)
      ?.subtypes.find((s) => s.id === structureSubtypeId)?.label;
    if (subtypeLabel) {
      return subtypeLabel;
    }
  }
  return structureName;
}

type BuildStructureOptionsParams = {
  structureOptions: { label: string; uniqueId: string }[];
  structureUniqueId: string | null;
  displayName: string | null;
  includeNewOption?: boolean;
};

export function buildStructureSelectOptions({
  structureOptions,
  structureUniqueId,
  displayName,
  includeNewOption = true
}: BuildStructureOptionsParams) {
  const options = structureOptions.map(({ label, uniqueId }) => ({
    label: uniqueId === structureUniqueId && displayName ? displayName : label,
    value: uniqueId
  }));

  if (includeNewOption) {
    options.push({ label: '+ New Structure', value: 'new' });
  }

  return options;
}

export function handleStructureChange(value: string) {
  if (value) {
    setQueryParam('structureId', value, true);
  }
}

export function isDetachedSpaceItem(itemId: number): boolean {
  const item = getItem(itemId);
  return !!item && DETACHED_SPACE_CODES.has(item.code);
}

export function isHallwaySpace(itemId: number): boolean {
  const item = getItem(itemId);
  return !!item && item.code === 'hallway';
}

export function isDetachedSpace(
  spaceId: string,
  structure: LevelType[]
): boolean {
  const space = findSpaceById(structure, spaceId);
  return !!space && isDetachedSpaceItem(space.itemId);
}
